import clsx from "clsx";
import PropTypes from "prop-types";
import { useState } from "react";
import { useIntl } from "react-intl";
import EditOutlined from "@mui/icons-material/EditOutlined";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { getAddressObjectName } from "./helpers/addressUtils";

const useStyles = makeStyles((theme) => ({
  table: {
    "& tbody tr:nth-of-type(odd)": {
      backgroundColor: "rgba(0,0,0,.03)",
    },
    borderRadius: 6,
    overflow: "hidden",
  },
  th: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    borderBottom: "none",
    color: theme.palette.text.secondary,
  },
  labelCell: {
    //width: 140,
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: "nowrap",
  },
  valueCell: {
    fontSize: 12,
    position: "relative",
    cursor: "text",
    paddingRight: 32,
    whiteSpace: "nowrap",
    "&:hover $editBtn": {
      opacity: 1,
    },
  },
  editBtn: {
    position: "absolute",
    right: 6,
    top: "50%",
    transform: "translateY(-50%)",
    opacity: 0,
    padding: 4,
    transition: "opacity 120ms ease",
  },
  input: {
    fontSize: 12,
  },
  empty: {
    opacity: 0.4,
    fontStyle: "italic",
  },
  dirtyCell: {
    backgroundColor: "rgba(255, 193, 7, 0.12)",
    boxShadow: "inset 3px 0 0 #f7bd02",
    transition: "background-color 150ms ease",
  },
  editingCell: {
    paddingTop: 0,
    paddingBottom: 0,
  },
}));

const FIELDS = [
  { key: "address1", labelId: "orderHistory.address" },
  { key: "address2", labelId: "orderHistory.section" },
  { key: "address3", labelId: "orderHistory.address2" },
  { key: "address4", labelId: "orderHistory.address3" },
  { key: "address5", labelId: "orderHistory.address4" },
  { key: "zip", labelId: "orderHistory.postCode" },
  { key: "city", labelId: "orderHistory.city" },
  { key: "country", labelId: "orderHistory.country" },
  { key: "reference", labelId: "orderHistory.reference" },
  { key: "email", labelId: "orderHistory.email" },
  { key: "telephone", labelId: "orderHistory.telephone" },
  { key: "contact", labelId: "orderHistory.contact" },
];

const AddressInfo = ({ tableTitle, address, getAddressLabel, editable, isFieldDirty, onChange, mode }) => {
  const classes = useStyles();
  const intl = useIntl();
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState("");

  const isPrintMode = (mode === "print");

  const startEdit = (key, value) => {
    if (!editable || isPrintMode) return;
    setEditingKey(key);
    setDraft(value || "");
  };

  const commit = () => {
    if (editingKey && onChange) {
      onChange(editingKey, draft);
    }
    setEditingKey(null);
  };

  return (
    <Table size="small" className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell className={classes.th} colspan={2}>{tableTitle}</TableCell>          
        </TableRow>
      </TableHead>
      <TableBody>
        {FIELDS.map(({ key, labelId }) => {
          const value = address?.[key];
          if (!value && !editable && !isPrintMode) return null;

          const isDirtyCell = editable && isFieldDirty(key);
          const isEditingCell = (editingKey === key);

          return (
            <TableRow key={key}>
              <TableCell className={classes.labelCell}>
                {getAddressLabel(key, intl.formatMessage({ id: labelId }))}
              </TableCell>

              <TableCell
                className={clsx(
                  classes.valueCell,
                  isDirtyCell && classes.dirtyCell,
                  editable && isEditingCell && classes.editingCell
                )}
                onClick={() => startEdit(key, value)}
              >
                {editable && !isEditingCell && !isPrintMode && (
                  <Tooltip title="Redigera">
                    <IconButton
                      size="small"
                      className={classes.editBtn}
                      onClick={(e) => {
                        e.stopPropagation();
                        startEdit(key, value);
                      }}
                    >
                      <EditOutlined fontSize="inherit" />
                    </IconButton>
                  </Tooltip>
                )}

                {editable && isEditingCell ? (
                  <TextField
                    autoFocus
                    value={draft}
                    variant="standard"
                    fullWidth
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") commit();
                      if (e.key === "Escape") setEditingKey(null);
                    }}
                    InputProps={{ className: classes.input }}
                  />
                ) : value ? (
                  value
                ) : (
                  <span className={classes.empty}>—</span>
                )}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

AddressInfo.propTypes = {
  tableTitle: PropTypes.string.isRequired,
  address: PropTypes.object.isRequired,
  editable: PropTypes.bool,
  onChange: PropTypes.func,
  mode: PropTypes.oneOf(["screen", "print"]),
};

AddressInfo.defaultProps = {
  editable: false,
  onChange: null,
  mode: "screen",
};

export default AddressInfo;
